import { usePageTitle } from '../../hooks/usePageTitle';
import SideBar from '../../components/global/SideBar';
import './index.css';
import { Loader, useCurrentUser, type PlanInterface } from '@beholos/holoscommon';
import { CalendarDays, Check, ExternalLink, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { getActivePlans } from '../../services/payment.service';
import { PaymentModal } from '../../components/membership/PaymentModal';
import { initMercadoPago } from '@mercadopago/sdk-react';
import { Link } from 'react-router-dom';
import { AdditionalBenefitsAccordion } from '../../components/membership/AdditionalBenefitsAccordion';
// import type { SmartFitDuration } from '../../components/membership/smartFit.constants';

const CYCLE_OPTIONS = [
  { value: 1, label: 'Mensual', discountLabel: '' },
  { value: 3, label: 'Trimestral', discountLabel: 'hasta -20%' },
  { value: 12, label: 'Anual', discountLabel: 'hasta -25%' },
] as const;

const getCycleDurationLabel = (cycleDuration?: number) => {
  if (cycleDuration === 12) return 'año';
  if (cycleDuration === 3) return 'trimestre';
  return 'mes';
};

const PLAN_METADATA: Record<string, { subtitle: string; partnerCount: number; features: string[] }> = {
  'Plan Digital': {
    subtitle: ' ',
    partnerCount: 0,
    features: [
      'Clases grabadas, programas y retos',
      'Incluye 3 cupos para plan familiar',
    ],
  },
  'Basic': {
    subtitle: 'Empieza tu journey de bienestar',
    partnerCount: 80,
    features: [
      'Clases grabadas, programas y retos',
      'Experiencias ilimitadas en +80 partners fitness',
      'Invita a 1 amig@ a clases hasta 1 vez por ciclo.',
      'Accede a eventos online en comunidad ',
      'Incluye 3 cupos para el plan familiar',

    ],
  },
  'Plus': {
    subtitle: 'Más variedad, más cobertura',
    partnerCount: 180,
    features: [
      'Basic, más:',
      'Experiencias ilimitadas en +180 partners fitness',
    ],
  },
  'Pro': {
    subtitle: 'Para quienes buscan más',
    partnerCount: 250,
    features: [
      'Plus, más:',
      'Experiencias ilimitadas en +250 partners',
      'Categorías como Psicología, Fitness Coaches, Alimentación Sana y Masajess',
    ],
  },
  'Ultra': {
    subtitle: 'La experiencia Holos al máximo',
    partnerCount: 300,
    features: [
      'Pro, más:',
      'Experiencias ilimitadas en +300 partners',
      'Nuevas categorías: Nutrición y Fisioterapia',
      'Acceso a eventos presenciales en comunidad',

    ],
  },
  'Holos Total': {
    subtitle: 'Cobertura total, salud integral',
    partnerCount: 400,
    features: [
      'Ultra, más:',
      'Experiencias ilimitadas en +400 partners',
      'Incluye Spa, Belleza y Cuidado Personal, Health Coaching y Asesoría Financiera',
    ],
  },
};

export default function UpdateMembership() {
  usePageTitle('Administrar Plan - Holos');

  useEffect(() => {
    initMercadoPago(import.meta.env.VITE_MERCADOPAGO_PUBLIC_KEY);
  }, []);

  const { user, loading, refetch } = useCurrentUser();
  const [selectedOption, setSelectedOption] = useState<'LEVEL_0' | 'LEVEL_1' | 'LEVEL_2' | 'LEVEL_3' | 'LEVEL_4' | 'LEVEL_5' | 'LEVEL_6'>();
  const [selectedPlan, setSelectedPlan] = useState<PlanInterface>();
  const [plans, setPlans] = useState<any[]>([]);
  const [selectedCycleDuration, setSelectedCycleDuration] = useState<number>(1);
  const [isOpenPayment, setIsOpenPayment] = useState(false);
  // const [smartFitInCart, setSmartFitInCart] = useState(false);
  // const [smartFitDuration, setSmartFitDuration] = useState<SmartFitDuration>('1 mes');

  useEffect(() => {
    getActivePlans().then((plans) => {
      const activePlans = (plans || []) as PlanInterface[];
      setPlans(activePlans);

      if (!activePlans.length) return;
      const preferredCycle = activePlans.find((plan: PlanInterface) => plan.cycleDuration === 1)?.cycleDuration ?? activePlans[0].cycleDuration ?? 1;
      setSelectedCycleDuration(preferredCycle);
    })
  }, [])

  const visiblePlans = plans.filter((plan) => plan.cycleDuration === selectedCycleDuration || !plan.cycleDuration);
  const plansToRender = visiblePlans.length ? visiblePlans : plans;

  const selectPlan = (selected: PlanInterface) => {
    setSelectedPlan(selected);
    setSelectedOption(selected?.accessTier || 'LEVEL_0');
    setIsOpenPayment(true);
  }

  const closePaymentModal = () => {
    setIsOpenPayment(false);
    setSelectedPlan(undefined);
    setSelectedOption(undefined);
  }

  const basePlan = user?.plan?.basePlan || undefined;
  const changePlan = user?.changePlan || false;
  const basePlanAccessTier = basePlan ? parseInt((basePlan?.accessTier || 'LEVEL_1').replace('LEVEL_', '')) : 0;
  const isLessThanBasePlan = basePlan ? (plan: PlanInterface) => basePlanAccessTier > parseInt((plan?.accessTier || 'LEVEL_1').replace('LEVEL_', '')) : () => false;
  const isCurrentPlan = (plan: PlanInterface) => plan._id === user?.plan?.id;
  const isCurrentBasePlan = (plan: PlanInterface) => basePlan ? plan.accessTier === basePlan.accessTier : false;
  const isCurrentChangedPlan = (plan: PlanInterface) => changePlan ? plan._id === changePlan.id : false;
  const hasNationalIdentityNumber = !!String(user?.nationalIdentityNumber ?? '').trim();
  const hasStreet = !!user?.address?.street?.trim();
  const hasRequiredProfileData = hasNationalIdentityNumber && hasStreet;
  const isPaymentDisabled = !hasRequiredProfileData;

  return (
    <div className="flex h-[calc(100vh-80px)] w-screen overflow-hidden">
      <div className="hidden md:block md:w-80 lg:w-96 h-full overflow-y-auto">
        <SideBar />
      </div>

      <div className="flex-1 h-full overflow-y-auto bg-gray-200 py-15 px-6">
        <div className="bg-gray-200 p-6 rounded max-w-8xl mx-auto">

          {loading ? (
            <Loader />
          ) : (
            <section className="bg-white border-2 border-gray-400 rounded-3xl my-6 p-6">
              {user?.hasPlan && (
                <div className="border-2 border-gray-400 rounded-lg flex flex-col w-full relative mb-10">
                  <div className="flex justify-between items-center border-b pb-3 p-4">
                    <div className="text-2xl font-bold font-juana">Administrar plan</div>
                    <div className="font-medium bg-prosper/70 rounded-4xl px-4 py-1 border border-gray-600">Activa</div>
                  </div>
                  <p className="text-sm text-gray-600 px-4 pt-3 pb-1">
                    Actualiza tu plan cuando lo necesites. Si haces un cambio, podrás elegir entre activarlo hoy o al final de tu ciclo actual.
                  </p>
                  <div className="bg-holos-light border border-gray-400 rounded-xl m-4 p-4 py-5 flex flex-col">
                    <div className="text-xs text-gray-500 mb-1">Tu plan actual</div>
                    <div className="text-xl font-bold">{user?.plan?.sharedPassPlan ? 'Pase de invitados' : user?.plan?.name}</div>
                  </div>
                  <div className="text-sm mb-4 px-4 flex items-center gap-2 text-gray-700">
                    <CalendarDays size={18} />
                    Renueva el {user?.nextResetDate ? format(parseISO(user.nextResetDate), 'dd/MM/yyyy') : ''}
                  </div>
                </div>
              )}

              <div className="mb-6 flex flex-col items-center gap-4 text-center">
                <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-gray-500">
                  <Sparkles className="h-3 w-3" />
                  Elige tu plan principal
                </span>
                <h2 className="font-juana text-3xl font-semibold tracking-tight text-gray-900 md:text-4xl">Planes Holos</h2>
              </div>

              <div className="mb-6 flex justify-center">
                <div className="inline-flex w-full max-w-xl rounded-full border border-gray-300 bg-gray-100 p-1 shadow-sm">
                  {CYCLE_OPTIONS.map((option) => {
                    const isSelected = selectedCycleDuration === option.value;
                    const discountLabel = basePlan
                      ? option.value === 3 ? 'hasta -5%' : option.value === 12 ? 'hasta -10%' : ''
                      : option.discountLabel;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setSelectedCycleDuration(option.value)}
                        className={`relative flex-1 rounded-full px-3 py-2 text-center transition-all duration-200 ${isSelected
                            ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-300'
                            : 'cursor-pointer text-gray-600 hover:text-gray-900'
                          }`}
                      >
                        <span className="block text-sm font-semibold">{option.label}</span>
                        <span className={`mt-0.5 block text-[10px] ${isSelected ? 'text-gray-600' : 'text-gray-500'}`}>
                          {discountLabel}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="mb-5 flex items-start gap-3 rounded-2xl border border-holos bg-holos-light px-4 py-4 md:px-6 md:py-5">
                <Sparkles className="mt-1 h-5 w-5 shrink-0 text-gray-800 md:h-6 md:w-6" />
                <div>
                  <p className="font-juana text-lg font-semibold leading-snug text-gray-900 md:text-2xl">
                    Acceso ilimitado a los partners de tu cobertura. Más partners, mayor bienestar.
                  </p>
                  <p className="mt-1 text-xs text-gray-600 md:text-sm">
                    Nuestros planes son autorrenovables, cancela cuando quieras.
                  </p>
                </div>
              </div>

              {!hasRequiredProfileData && (
                <div className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900">
                  <p className="text-sm md:text-base">
                    Para continuar con la compra, primero completa tu DNI y dirección en tu cuenta.
                  </p>
                  <Link
                    to="/mi-cuenta"
                    className="mt-3 inline-flex items-center rounded-full border border-amber-800 px-4 py-1.5 text-sm font-medium hover:bg-amber-100 transition-colors"
                  >
                    Completar mi perfil
                  </Link>
                </div>
              )}

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-5">
                {plansToRender.map((plan: PlanInterface) => {
                  const meta = PLAN_METADATA[plan.name] ?? { subtitle: '', partnerCount: 0, features: [] };
                  const cycleDuration = plan.cycleDuration ?? selectedCycleDuration;
                  const planCycleLabel = getCycleDurationLabel(cycleDuration);
                  const isHighTierPlan = plan.name === 'Ultra' || plan.name === 'Holos Total';
                  const discountRate = basePlan
                    ? cycleDuration === 3 ? 0.05 : cycleDuration === 12 ? 0.1 : 0
                    : cycleDuration === 3 ? (isHighTierPlan ? 0.1 : 0.2) : cycleDuration === 12 ? (isHighTierPlan ? 0.15 : 0.25) : 0;
                  const currentPriceRaw = Math.max(0, basePlan ? plan.price - basePlan.price : plan.price);
                  const currentPrice = Math.round(currentPriceRaw);
                  const originalPrice = discountRate > 0 ? Math.round(currentPriceRaw / (1 - discountRate)) : null;
                  const equivalentMonthlyPrice = cycleDuration > 1 ? Math.round(currentPriceRaw / cycleDuration) : null;
                  const isFeatured = plan.name === 'Ultra';
                  const isCurrent = isCurrentPlan(plan);
                  const isCurrentBase = isCurrentBasePlan(plan);
                  const isChangedPlan = isCurrentChangedPlan(plan);
                  const isDisabled = isCurrent || isChangedPlan || isCurrentBase || (basePlan && isLessThanBasePlan(plan));
                  return (
                    <div
                      key={plan._id}
                      className={`relative flex h-full flex-col rounded-3xl border p-5 transition-all duration-300 ${isCurrent
                          ? 'border-gray-900 bg-holos-light/40 shadow-md ring-2 ring-gray-900'
                          : isFeatured
                            ? 'border-gray-900 bg-white shadow-md ring-2 ring-gray-400 hover:-translate-y-1 hover:shadow-lg'
                            : 'border-gray-200 bg-white shadow-sm hover:-translate-y-1 hover:shadow-lg'
                        }`}
                    >
                      {isFeatured && !isCurrent && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wider bg-gray-900 text-white">
                          Más popular
                        </span>
                      )}

                      {isCurrentBase && (
                        <span className="mb-2 self-start rounded-full bg-holos-light border border-gray-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-700">
                          Elegido por tu empresa
                        </span>
                      )}
                      {isCurrent && !isCurrentBase && (
                        <span className="mb-2 self-start rounded-full bg-holos-light border border-gray-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-700">
                          Plan actual
                        </span>
                      )}
                      {isChangedPlan && !isCurrentBase && (
                        <span className="mb-2 self-start rounded-full bg-holos-light border border-gray-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-700">
                          Próximo plan
                        </span>
                      )}

                      <div className="mb-3">
                        <h3 className="font-juana text-xl font-semibold text-gray-900">{plan.name}</h3>
                        <p className="mt-0.5 text-xs text-gray-500">{meta.subtitle}</p>
                      </div>

                      <div className="mb-4 flex items-center justify-between rounded-xl px-3 py-2 bg-holos-light">
                        <span className="text-[10px] font-medium uppercase tracking-wider text-gray-600">Cobertura partners</span>
                        <span className="font-juana text-lg font-semibold text-gray-900">{meta.partnerCount}</span>
                      </div>

                      <a
                        href={`/user/partners?plan=${encodeURIComponent(plan.name.toLowerCase())}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="-mt-2 mb-4 inline-flex items-center gap-1 self-start text-[11px] font-medium text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline"
                      >
                        Ver partners
                        <ExternalLink className="h-3 w-3" />
                      </a>

                      {!isLessThanBasePlan(plan) && (
                        <div className="mb-4 mt-2 flex min-h-[132px] flex-col gap-1.5">
                          {basePlan && selectedCycleDuration === 1 && (
                            <p className="text-xs text-gray-400 line-through">S/. {Math.round(plan.price)}</p>
                          )}
                          {discountRate > 0 && originalPrice !== null && (
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-[13.5px] text-gray-500 line-through">
                                S/. {originalPrice} / {planCycleLabel}
                              </span>
                              <span className="rounded-md bg-black px-2 py-0.5 text-[10.5px] font-bold text-white">
                                -{Math.round(discountRate * 100)}%
                              </span>
                            </div>
                          )}

                          <div className="flex items-baseline gap-1.5">
                            <span className="text-sm text-gray-700">S/.</span>
                            <span className="font-juana text-[38px] font-semibold leading-none tracking-[-1px] text-gray-900">
                              {currentPrice}
                            </span>
                            <span className="text-xs text-gray-500">/ {planCycleLabel}</span>
                          </div>

                          <span className="text-[12.5px] leading-relaxed text-gray-500">
                            {cycleDuration === 1
                              ? 'Facturación mensual · cancela cuando quieras'
                              : `Pago único cada ${cycleDuration} meses`}
                          </span>

                          {cycleDuration > 1 && equivalentMonthlyPrice !== null && (
                            <span className="mt-1 inline-flex self-start rounded-full bg-[#f0fa7e] px-3 py-1 text-xs font-semibold text-black">
                              Equivale a S/. {equivalentMonthlyPrice}/mes
                            </span>
                          )}
                        </div>
                      )}

                      <ul className="mb-5 flex-1 space-y-2">
                        {meta.features.map((feature, i) => (
                          <li key={i} className="flex gap-2 text-xs text-gray-700">
                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-900" strokeWidth={3} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        disabled={isDisabled || isPaymentDisabled}
                        onClick={() => !isDisabled && !isPaymentDisabled && selectPlan(plan)}
                        className={`w-full rounded-full px-4 py-2 text-xs font-semibold transition-all disabled:cursor-not-allowed ${isCurrent || isChangedPlan || isCurrentBase
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : isFeatured
                              ? 'cursor-pointer bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50'
                              : 'cursor-pointer border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white disabled:opacity-50'
                          }`}
                      >
                        {isCurrent ? 'Plan actual' : isChangedPlan ? 'Próximo plan' : isCurrentBase ? 'Plan base actual' : 'Seleccionar plan'}
                      </button>
                    </div>
                  );
                })}
              </div>

              <AdditionalBenefitsAccordion
              // selectedDuration={smartFitDuration}
              // onSelectDuration={setSmartFitDuration}
              // smartFitInCart={smartFitInCart}
              // onAddToCart={() => setSmartFitInCart(true)}
              />
            </section>
          )}
        </div>
      </div>

      {selectedOption !== undefined && selectedPlan && <PaymentModal
        isOpen={isOpenPayment}
        closeModal={closePaymentModal}
        selectedOption={selectedOption}
        selectedPlan={selectedPlan}
        basePlan={basePlan}
        // smartFitInCart={smartFitInCart}
        // smartFitDuration={smartFitDuration}
        // setSmartFitDuration={setSmartFitDuration}
        refetch={refetch}
      />}
    </div >
  );
}
